import React, {
    useCallback, useEffect, useRef, useState
} from 'react'
import PropTypes from 'prop-types'
import {
    Button, Link, Spinner, Validator, Typography
} from '@telus/koodo-ui-kit'

const OTP_LENGTH = 6
const TIMER_INTERVAL_MS = 1000

// Auto-validate only once the user has stopped typing for this long, so a
// complete code (or an edited one) is checked a beat after the last keystroke.
const OTP_VERIFY_DEBOUNCE_MS = 250

/**
 * OtpStep — second step of the verify flow.
 *
 * Renders the 6-digit code entry. The countdown timer is owned here because
 * it is purely a UI concern (it resets every time the modal opens or a resend
 * happens). All API calls (verifyCode, onResend) are passed in from VerifyPage.
 */
const OtpStep = ({
    title,
    email,
    otpCode,
    onOtpChange,
    onVerify,
    onResend,
    onUseOtherEmail,
    isLoading,
    isDisabled,
    expirationMinutes,
    resetTimerSignal,
    resetFieldSignal,
    attemptsLeft,
    youHaveAttemptsLeftFirst,
    youHaveAttemptsLeftSecond,
    sentCodeText,
    sentCodeBoldText,
    resendLabel,
    pleaseEnterCodeText,
    codeExpiresText,
    codeExpiredText,
    userBlockedHeading,
    useOtherEmailLabel,
    isUserBlocked,
    showWrongCode,
    wrongCodeText,
    showOtpExpired,
    otpExpiredText,
    showMaxAttempts,
    maxAttemptsText,
    showResendError,
    resendErrorText,
    showUserBlockedError,
    userBlockedText,
    selfServeEmailText
}) => {
    const totalSeconds = expirationMinutes * 60
    const [time, setTime] = useState(totalSeconds)
    const [isExpired, setIsExpired] = useState(false)
    const [isResendPending, setIsResendPending] = useState(false)

    // Resend is locked while the expiry countdown is still running, and while
    // a resend request is in flight. The lock is applied through the Link's
    // `accessibilityDisabled` prop — UDS/react-native-web renders it as
    // aria-disabled + tabindex="-1", suppresses the click handler and sets
    // pointer-events: none (verified against the built kit; the kit's own
    // `disabled`/`aria-disabled` props are dropped before reaching the DOM).
    const isTimerRunning = totalSeconds > 0 && !isExpired
    const isResendLocked = isTimerRunning || isResendPending

    // Autofocus the first Validator input on mount and after a resend, which
    // remounts the kit via `resetFieldSignal` to clear it for the new code.
    const validatorContainerRef = useRef(null)
    useEffect(() => {
        if (isDisabled || isUserBlocked) return undefined
        const id = setTimeout(() => {
            validatorContainerRef.current?.querySelector('input')?.focus()
        }, 50)
        return () => clearTimeout(id)
    }, [resetFieldSignal, isDisabled, isUserBlocked])

    // Reset the countdown DURING render (not in the effect below) whenever a
    // resend happens or expirationMinutes changes: an effect only runs after
    // commit, so an expired `isExpired` would otherwise survive into one
    // committed frame and momentarily release the resend lock.
    const timerKey = `${resetTimerSignal}:${totalSeconds}`
    const [prevTimerKey, setPrevTimerKey] = useState(timerKey)
    if (prevTimerKey !== timerKey) {
        setPrevTimerKey(timerKey)
        setTime(totalSeconds)
        setIsExpired(false)
    }

    // (Re)start the interval whenever the countdown above was reset.
    useEffect(() => {
        if (!totalSeconds) return undefined
        const timer = setInterval(() => {
            setTime((prev) => {
                if (prev <= 1) {
                    clearInterval(timer)
                    setIsExpired(true)
                    return 0
                }
                return prev - 1
            })
        }, TIMER_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [resetTimerSignal, totalSeconds])

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }

    const handleResend = useCallback(async () => {
        // Backstop for the accessibilityDisabled lock on the Link, in case a
        // click ever reaches the handler anyway.
        if (isResendLocked) return
        setIsResendPending(true)
        try {
            await onResend()
        } finally {
            setIsResendPending(false)
        }
    }, [isResendLocked, onResend])

    // The first complete code validates immediately (fast happy path). Once an
    // attempt has failed, the user is correcting digits, so we switch to a
    // debounce and only re-validate after they stop typing. A resend issues a
    // fresh code and returns to immediate validation.
    const verifyDebounceRef = useRef(null)
    const hasFailedRef = useRef(false)
    useEffect(() => () => clearTimeout(verifyDebounceRef.current), [])
    useEffect(() => { hasFailedRef.current = false }, [resetFieldSignal])

    const handleOtpChange = useCallback((value) => {
        onOtpChange(value)
        clearTimeout(verifyDebounceRef.current)

        const canVerify = value.length === OTP_LENGTH && !isDisabled && !isLoading && !isUserBlocked
        if (!canVerify) return

        if (hasFailedRef.current) {
            verifyDebounceRef.current = setTimeout(() => onVerify(value), OTP_VERIFY_DEBOUNCE_MS)
        } else {
            onVerify(value)
        }
    }, [onOtpChange, onVerify, isDisabled, isLoading, isUserBlocked])

    const getErrorText = () => {
        if (showWrongCode) return wrongCodeText
        if (showOtpExpired) return otpExpiredText
        if (showMaxAttempts) return maxAttemptsText
        if (showResendError) return resendErrorText
        if (showUserBlockedError) return userBlockedText
        return ''
    }
    const errorText = getErrorText()

    // On a fresh failed attempt the entered digits are intentionally kept (not
    // cleared). Drop the cursor back into the first box and highlight its digit
    // so the user can either retype the whole code — overwriting left-to-right —
    // or tab to the single box they got wrong. Skipped when the field is locked
    // (max attempts / blocked), where there is nothing to retry inline.
    const prevErrorRef = useRef('')
    useEffect(() => {
        const isFreshError = Boolean(errorText) && errorText !== prevErrorRef.current
        prevErrorRef.current = errorText
        if (!isFreshError || isDisabled || isUserBlocked) return undefined
        // A retryable failure → debounce subsequent complete entries.
        hasFailedRef.current = true
        const id = setTimeout(() => {
            const firstInput = validatorContainerRef.current?.querySelector('input')
            firstInput?.focus()
            firstInput?.select?.()
        }, 50)
        return () => clearTimeout(id)
    }, [errorText, isDisabled, isUserBlocked])

    return (
        <div className="flex flex-col w-full mx-auto gap-6">
            <Typography heading="h1" variant={{ size: 'h3' }} block>
                {title}
            </Typography>

            {!isUserBlocked && (
                <Typography variant={{ size: 'medium' }} block>
                    {sentCodeText}{sentCodeBoldText ? ' ' : ''}
                    {sentCodeBoldText && (
                        <Typography variant={{ size: 'medium', weight: 'semibold' }}>
                            {sentCodeBoldText}
                        </Typography>
                    )}{' '}
                    {/* No href while locked: the suppressed onClick can no
                        longer preventDefault, so a click reaching the anchor
                        (Enter on a still-focused link, programmatic click)
                        would otherwise navigate to "/". */}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                        href={isResendLocked ? undefined : '/'}
                        onClick={(e) => { e.preventDefault(); handleResend() }}
                        testId="resend-otp"
                        accessibilityDisabled={isResendLocked || undefined}
                        tokens={{
                            color: isResendLocked ? '#666666' : undefined,
                            fontWeight: 600
                        }}
                    >
                        {resendLabel}
                    </Link>
                </Typography>
            )}

            <div>
                {isUserBlocked ? (
                    <div className="text-center my-5">
                        <Typography heading="h2" variant={{ size: 'h2' }} block tokens={{ color: 'var(--color-brand-purple)' }}>
                            {userBlockedHeading}
                        </Typography>
                    </div>
                ) : (
                    <>
                        <Typography block tokens={{ fontSize: 18, lineHeight: 28 / 18, fontWeight: '600' }}>
                            {pleaseEnterCodeText}{' '}
                            <span className="text-brand-purple">({email})</span>
                        </Typography>

                        <Typography variant={{ size: 'medium' }} block>
                            {isExpired ? (
                                codeExpiredText
                            ) : (
                                <>
                                    {codeExpiresText}{' '}
                                    <span className="text-brand-purple">{formatTime(time)}</span>
                                </>
                            )}
                        </Typography>
                    </>
                )}
            </div>

            <div className="flex flex-col items-center gap-4 sm:flex-row">
                <div ref={validatorContainerRef}>
                    <Validator
                        key={resetFieldSignal}
                        value={otpCode}
                        onChange={handleOtpChange}
                        inactive={isDisabled || isLoading || isUserBlocked}
                        variant={{ error: true }}
                        feedback={errorText}
                        validation={errorText ? 'error' : undefined}
                        testId="otp-validator"
                    />
                </div>
                <Spinner
                    label=""
                    variant={{ size: 'large' }}
                    show={isLoading}
                    inline
                    testId="otp-spinner"
                />
            </div>

            {!isUserBlocked && !showMaxAttempts && (
                <Typography tokens={{ fontSize: 18, lineHeight: 28 / 18, fontWeight: '500' }} block>
                    {youHaveAttemptsLeftFirst}{' '}
                    <span className="text-brand-pink">{attemptsLeft}</span>{' '}
                    {youHaveAttemptsLeftSecond}
                </Typography>
            )}

             <Typography variant={{ size: 'medium' }} block>
                {selfServeEmailText}
             </Typography>

            <Button
                onPress={onUseOtherEmail}
                variant={{ width: 'full' }}
                testId="use-other-email"
            >
                {useOtherEmailLabel}
            </Button>
        </div>
    )
}

OtpStep.propTypes = {
    title: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    otpCode: PropTypes.string.isRequired,
    onOtpChange: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onResend: PropTypes.func.isRequired,
    onUseOtherEmail: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
    expirationMinutes: PropTypes.number.isRequired,
    resetTimerSignal: PropTypes.number,
    resetFieldSignal: PropTypes.number,
    attemptsLeft: PropTypes.number.isRequired,
    youHaveAttemptsLeftFirst: PropTypes.string,
    youHaveAttemptsLeftSecond: PropTypes.string,
    sentCodeText: PropTypes.string,
    sentCodeBoldText: PropTypes.string,
    resendLabel: PropTypes.string,
    pleaseEnterCodeText: PropTypes.string,
    codeExpiresText: PropTypes.string,
    codeExpiredText: PropTypes.string,
    verifyLabel: PropTypes.string,
    userBlockedHeading: PropTypes.string,
    useOtherEmailLabel: PropTypes.string,
    isUserBlocked: PropTypes.bool,
    showWrongCode: PropTypes.bool,
    wrongCodeText: PropTypes.string,
    showOtpExpired: PropTypes.bool,
    otpExpiredText: PropTypes.string,
    showMaxAttempts: PropTypes.bool,
    maxAttemptsText: PropTypes.string,
    showResendError: PropTypes.bool,
    resendErrorText: PropTypes.string,
    showUserBlockedError: PropTypes.bool,
    userBlockedText: PropTypes.string,
    selfServeEmailText: PropTypes.string
}

OtpStep.defaultProps = {
    isLoading: false,
    isDisabled: false,
    resetTimerSignal: null,
    resetFieldSignal: 0,
    youHaveAttemptsLeftFirst: '',
    youHaveAttemptsLeftSecond: '',
    sentCodeText: '',
    sentCodeBoldText: '',
    resendLabel: '',
    pleaseEnterCodeText: '',
    codeExpiresText: '',
    codeExpiredText: '',
    verifyLabel: 'Verify',
    userBlockedHeading: '',
    useOtherEmailLabel: '',
    isUserBlocked: false,
    showWrongCode: false,
    wrongCodeText: '',
    showOtpExpired: false,
    otpExpiredText: '',
    showMaxAttempts: false,
    maxAttemptsText: '',
    showResendError: false,
    resendErrorText: '',
    showUserBlockedError: false,
    userBlockedText: '',
    selfServeEmailText: ''
}

export default OtpStep
